import type { Session } from '@supabase/supabase-js'
import { createStore, useStore } from 'zustand'

import { AuthService } from '@/modules/auth'
import {
  InvalidCredentialsError,
  SignInAccountNotFoundError,
  SignInError,
} from '@/modules/errors'
import { eventTracker } from '@/services/tracking'

type AuthStoreActions = {}
type AuthStoreMutations = {
  signIn: ({ email }: { email: string }) => Promise<void>
  signUp: ({
    email,
    metadata,
  }: {
    email: string
    metadata?: object
  }) => Promise<void>
  signOut: () => Promise<void>
  verifyOtp: ({ code, email }: { code: string; email: string }) => Promise<void>
}
type AuthStoreLoaders = {
  getSession: () => Promise<Session | null>
}

export interface AuthStore {
  session: Session | null
  user: AuthService.User | null
  actions: AuthStoreActions
  mutations: AuthStoreMutations
  mutating: boolean
  loaders: AuthStoreLoaders
}

const authStore = createStore<AuthStore>()((set, get) => ({
  session: null,
  user: null,
  mutating: false,
  actions: {},
  mutations: {
    signIn: async ({ email }) => {
      const { error } = await AuthService.signIn({ email })
      if (error) {
        if (
          error?.code === 'otp_disabled' &&
          error.message.includes('Signups not allowed for otp')
        ) {
          throw new SignInAccountNotFoundError()
        }

        console.error(error)
        throw new SignInError(error.message)
      }
    },

    signUp: async ({ email, metadata }) => {
      set({ mutating: true })
      await AuthService.signUp({ email, metadata })
      set({ mutating: false })
    },

    verifyOtp: async ({ code, email }) => {
      const { data, error } = await AuthService.verifyOtp({ code, email })
      if (error || !data.session) {
        set({ session: null })
        throw new InvalidCredentialsError()
      }

      const { confirmed_at, created_at, user_metadata } = data.session.user

      eventTracker.signedIn({
        email,
        confirmed_at,
        created_at,
        ...user_metadata,
      })

      const { user } = await AuthService.getUser()
      set({ session: data.session, user: user || null })
    },

    signOut: async () => {
      await AuthService.signOut()
      set({ session: null })
    },
  },

  loaders: {
    getSession: async () => {
      const session = get().session
      if (session) return session

      const { data } = await AuthService.getSession()
      const { user } = await AuthService.getUser()
      set({ session: data.session, user: user || null })
      return data.session
    },
  },
}))

const useAuthStore = <T>(selector: (state: AuthStore) => T): T =>
  useStore(authStore, selector)

export const authLoaders = () => authStore.getState().loaders

export const useUser = () => useAuthStore((state) => state.user)
export const useAuthMutations = () => useAuthStore((state) => state.mutations)
export const useAuthLoaders = () => useAuthStore((state) => state.loaders)
export const useAuthMutating = () => useAuthStore((state) => state.mutating)
